import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Brain, TrendingUp, TrendingDown, CheckCircle2, XCircle, Clock,
  Sparkles, AlertTriangle, Award, ShieldCheck, Activity,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  PageShell, PageHeader, Card, Section, Pill, Loader, EmptyState, Alert,
  NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";

/**
 * AdvisorMemory — la mémoire permanente d'ALTIORA sur le conseiller.
 *
 * Répond à : « Que sais-tu de moi ? Qu'a-t-il changé depuis un mois ?
 * Quelles habitudes me font progresser ? Quelles habitudes me ralentissent ? »
 */
export default function AdvisorMemory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/memory/advisor/overview")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageShell testid="memory-loading"><TopBar /><div className="p-10"><Loader label="Compilation de ma mémoire…" /></div></PageShell>;

  return (
    <PageShell testid="memory-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="ALTIORA · Mémoire permanente"
          title="Ce que je sais de toi"
          subtitle="Plus tu utilises ALTIORA, plus je te connais. Cette page rend visible mon apprentissage : ce que j'ai appris, ce qui a changé, tes forces émergentes, tes habitudes qui te font progresser et celles qui te ralentissent."
          testid="memory-header"
        />

        {error && <div className="mt-6"><Alert variant="danger">{error}</Alert></div>}

        {data && (
          <div className="mt-10 space-y-10">
            <WhatILearned data={data.what_i_learned} />
            <WhatChanged data={data.what_changed_since_last_month} />
            <Habits data={data.habits} />
          </div>
        )}
      </div>
    </PageShell>
  );
}


function StatCard({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <div className="rounded-xl p-4 bg-white"
         style={{ border: `1px solid ${CHAMPAGNE}33` }}
         data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em]"
           style={{ color: SLATE }}>
        <Icon className="h-3 w-3" strokeWidth={1.8} /> {label}
      </div>
      <div className="mt-1 font-serif text-2xl tabular-nums" style={{ color: accent || NAVY }}>
        {value}
      </div>
      {hint && <div className="text-[11px]" style={{ color: SLATE }}>{hint}</div>}
    </div>
  );
}


function WhatILearned({ data }) {
  return (
    <div data-testid="memory-learned">
      <div className="flex items-baseline gap-3 mb-4">
        <Brain className="h-4 w-4" style={{ color: CHAMPAGNE }} strokeWidth={1.8} />
        <h2 className="font-serif text-2xl tracking-tight" style={{ color: NAVY }}>
          Ce que j'ai appris sur toi
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Activity} label="Recommandations traitées" value={data.total_recommendations_processed}
                  hint={`${data.completion_ratio_pct}% complétées`} accent={NAVY} testid="memory-total" />
        <StatCard icon={CheckCircle2} label="Actions faites" value={data.actions_completed}
                  accent={GREEN_LEAF} testid="memory-done" />
        <StatCard icon={XCircle} label="Actions ignorées" value={data.actions_dismissed}
                  accent={RED_LEAF} testid="memory-dismissed" />
        <StatCard icon={Clock} label="Heures de pointe (UTC)"
                  value={data.peak_hours_utc.length ? data.peak_hours_utc.map(h => `${h}h`).join(" · ") : "—"}
                  hint="quand tu marques faits"
                  accent={GOLD} testid="memory-peak" />
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="memory-signals">
        {Object.entries(data.signals).map(([k, v]) => (
          <div key={k} className="rounded-md px-3 py-2 text-[12px]"
               style={{ background: `${CHAMPAGNE}12`, border: `1px solid ${CHAMPAGNE}22` }}>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: SLATE }}>
              {k.replace(/_/g, " ")}
            </div>
            <div className="font-serif text-sm tabular-nums" style={{ color: NAVY }}>
              {typeof v === "boolean" ? (v ? "Oui" : "Non") : v}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function WhatChanged({ data }) {
  return (
    <div data-testid="memory-changed">
      <div className="flex items-baseline gap-3 mb-4">
        <TrendingUp className="h-4 w-4" style={{ color: CHAMPAGNE }} strokeWidth={1.8} />
        <h2 className="font-serif text-2xl tracking-tight" style={{ color: NAVY }}>
          Ce qui a changé depuis le mois dernier
        </h2>
      </div>

      {!data.has_baseline && (
        <EmptyState icon={Clock}
          title="Pas encore de comparaison possible."
          description="Je construis ta base de comparaison au fil des jours. Reviens dans quelques semaines pour voir ton évolution mensuelle."
          testid="memory-changed-empty" />
      )}

      {data.has_baseline && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.new_strengths.length > 0 && (
            <div className="rounded-xl p-4"
                 style={{ background: `${GREEN_LEAF}18`, border: `1px solid ${GREEN_LEAF}44` }}
                 data-testid="memory-new-strengths">
              <div className="flex items-center gap-2 mb-2" style={{ color: "#065F46" }}>
                <Sparkles className="h-4 w-4" strokeWidth={2} />
                <div className="text-[10px] font-mono uppercase tracking-[0.22em]">
                  Nouvelles forces (score ≥ 70)
                </div>
              </div>
              <ul className="text-[13px] space-y-1" style={{ color: NAVY }}>
                {data.new_strengths.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          )}
          {data.new_weaknesses.length > 0 && (
            <div className="rounded-xl p-4"
                 style={{ background: `${RED_LEAF}22`, border: `1px solid ${RED_LEAF}55` }}
                 data-testid="memory-new-weaknesses">
              <div className="flex items-center gap-2 mb-2" style={{ color: "#7F1D1D" }}>
                <AlertTriangle className="h-4 w-4" strokeWidth={2} />
                <div className="text-[10px] font-mono uppercase tracking-[0.22em]">
                  Nouvelles faiblesses (score &lt; 40)
                </div>
              </div>
              <ul className="text-[13px] space-y-1" style={{ color: NAVY }}>
                {data.new_weaknesses.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          )}
          {data.changes.length > 0 && (
            <div className="md:col-span-2 rounded-xl p-4 bg-white"
                 style={{ border: `1px solid ${CHAMPAGNE}33` }}
                 data-testid="memory-changes-list">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2" style={{ color: CHAMPAGNE }}>
                Variations principales
              </div>
              <div className="space-y-1.5">
                {data.changes.map((c) => (
                  <div key={c.dimension} className="flex items-center justify-between text-[13px]">
                    <span style={{ color: NAVY }}>{c.dimension}</span>
                    <span className="tabular-nums flex items-center gap-1.5"
                          style={{ color: c.delta > 0 ? "#065F46" : "#B91C1C" }}>
                      {c.delta > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {c.from} → {c.to} ({c.delta > 0 ? "+" : ""}{c.delta})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function Habits({ data }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="memory-habits">
      <div className="rounded-xl p-5 bg-white" style={{ border: `1px solid ${GREEN_LEAF}44` }}
           data-testid="memory-helping">
        <div className="flex items-center gap-2 mb-3">
          <Award className="h-4 w-4" style={{ color: "#065F46" }} strokeWidth={1.8} />
          <h3 className="font-serif text-lg tracking-tight" style={{ color: NAVY }}>
            Habitudes qui te font progresser
          </h3>
        </div>
        {data.helping_you_progress.length === 0 ? (
          <p className="text-[13px]" style={{ color: SLATE }}>
            Aucune habitude marquante détectée pour l'instant. Continue à utiliser ALTIORA quotidiennement — je saurai bientôt.
          </p>
        ) : (
          <ul className="text-[13px] space-y-2" style={{ color: NAVY }}>
            {data.helping_you_progress.map((h, i) => <li key={i}>· {h}</li>)}
          </ul>
        )}
      </div>
      <div className="rounded-xl p-5 bg-white" style={{ border: `1px solid ${RED_LEAF}44` }}
           data-testid="memory-slowing">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-4 w-4" style={{ color: "#B91C1C" }} strokeWidth={1.8} />
          <h3 className="font-serif text-lg tracking-tight" style={{ color: NAVY }}>
            Habitudes qui te ralentissent
          </h3>
        </div>
        {data.slowing_you_down.length === 0 ? (
          <p className="text-[13px]" style={{ color: SLATE }}>
            Aucun frein détecté — bravo. Je reste vigilant sur tes cadences.
          </p>
        ) : (
          <ul className="text-[13px] space-y-2" style={{ color: NAVY }}>
            {data.slowing_you_down.map((h, i) => <li key={i}>· {h}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
